import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';

import { DataCell, DeleteCheckboxCell, EditableCell, RowIndexCell } from './BulkUploadTableCells';

const createColumns = (columnDefs, editable, deletable) => {
    const CellComponent = editable ? EditableCell : DataCell;

    const cols = columnDefs.map((def) => ({
        id: def.key,
        accessorFn: (row) => row.data[def.key],
        header: def.label,
        cell: CellComponent,
    }));

    cols.unshift({
        id: '_rowIndex',
        header: '#',
        cell: RowIndexCell,
        size: 50,
    });

    if (deletable) {
        cols.unshift({
            id: '_delete',
            header: '',
            cell: DeleteCheckboxCell,
            size: 50,
        });
    }

    return cols;
};

/**
 * Table for displaying bulk upload data with validation errors, inline editing, and row deletion.
 *
 * @param {Object} props
 * @param {Array<{ key: string, label: string }>} props.columnDefs - Column definitions
 * @param {Array<{ rowIndex: number, data: Object }>} props.rows - Row data from the API
 * @param {Map<string, { row: number, field: string, message: string }>} props.errorsByCell - Error lookup map
 * @param {Object} [props.pendingEdits] - Map of pending edits by rowIndex -> { field: value }
 * @param {Function} [props.updateCell] - Callback to update a cell value: (rowIndex, field, value) => void
 * @param {Set<number>} [props.pendingDeletes] - Set of row indices marked for deletion
 * @param {Function} [props.toggleRowDelete] - Callback to toggle row deletion: (rowIndex) => void
 * @param {string} [props.className] - Additional classes
 */
export function BulkUploadTable({
    className,
    columnDefs,
    errorsByCell,
    pendingDeletes,
    pendingEdits,
    rows,
    toggleRowDelete,
    updateCell,
}) {
    const editable = Boolean(updateCell);
    const deletable = Boolean(toggleRowDelete);

    // Memoize columns based on columnDefs, editable, and deletable mode
    const columns = useMemo(() => createColumns(columnDefs, editable, deletable), [columnDefs, editable, deletable]);

    const table = useReactTable({
        data: rows,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(row.rowIndex),
        meta: {
            errorsByCell,
            pendingDeletes,
            pendingEdits,
            toggleRowDelete,
            updateCell,
        },
    });

    if (rows.length === 0) {
        return (
            <div className={cn('rounded-lg border p-8 text-center text-muted-foreground', className)}>
                No data to display
            </div>
        );
    }

    return (
        <div className={cn('rounded-lg border', className)}>
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHead
                                    key={header.id}
                                    className="text-center"
                                    style={{
                                        width: header.column.getSize() !== 150 ? header.column.getSize() : undefined,
                                    }}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id} className="text-center">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
